import { type RefObject, useEffect } from 'react';

/**
 * Lets a left-anchored drawer follow the finger and close on a left
 * swipe. Vertical drags are left to the scroller: the gesture locks
 * to an axis after the first few pixels.
 */
export function useSwipeToClose(
  ref: RefObject<HTMLElement>,
  enabled: boolean,
  onClose: () => void,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    let x0 = 0;
    let y0 = 0;
    let t0 = 0;
    let dx = 0;
    let axis: 'x' | 'y' | null = null;

    const reset = (): void => {
      el.style.transform = '';
      el.style.transition = '';
    };
    const onStart = (e: TouchEvent): void => {
      const t = e.touches[0];
      x0 = t.clientX;
      y0 = t.clientY;
      t0 = performance.now();
      dx = 0;
      axis = null;
    };
    const onMove = (e: TouchEvent): void => {
      const t = e.touches[0];
      const mx = t.clientX - x0;
      const my = t.clientY - y0;
      if (!axis && Math.hypot(mx, my) > 8) axis = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
      if (axis !== 'x') return;
      e.preventDefault();
      dx = Math.min(0, mx);
      el.style.transition = 'none';
      el.style.transform = `translateX(${dx}px)`;
    };
    const onEnd = (): void => {
      if (axis !== 'x') return;
      const velocity = -dx / Math.max(1, performance.now() - t0); // px/ms
      reset();
      if (dx < -el.offsetWidth * 0.3 || velocity > 0.5) onClose();
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', reset);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', reset);
      reset();
    };
  }, [ref, enabled, onClose]);
}

/**
 * Installed-app gesture: a rightward swipe starting at the left screen
 * edge opens the drawer. Only in standalone mode, because in a browser
 * tab the same edge swipe is the system "back".
 */
export function useEdgeSwipeOpen(enabled: boolean, onOpen: () => void): void {
  useEffect(() => {
    if (!enabled || !window.matchMedia('(display-mode: standalone)').matches) return;
    let x0 = -1;
    let y0 = 0;
    const onStart = (e: TouchEvent): void => {
      const t = e.touches[0];
      x0 = t.clientX <= 24 ? t.clientX : -1;
      y0 = t.clientY;
    };
    const onEnd = (e: TouchEvent): void => {
      if (x0 < 0) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0;
      if (dx > 60 && Math.abs(t.clientY - y0) < dx) onOpen();
      x0 = -1;
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, [enabled, onOpen]);
}
